import { useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import { TextInput } from "../components/ui/field.js";
import { ErrorState } from "../components/ui/states.js";
import { useI18n } from "../i18n/index.js";
import { ApiClientError } from "../lib/apiClient.js";
import { useSession } from "../lib/session.js";

interface FieldErrors {
  email?: string;
  password?: string;
  general?: string;
}

export function LoginPage() {
  const { status, login, user } = useSession();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);

  if (status === "authenticated" && user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setErrors({});
    try {
      await login(email, password);
    } catch (err) {
      if (!(err instanceof ApiClientError)) {
        setErrors({ general: t("auth.unreachable") });
      } else {
        const fields = err.fieldIssues();
        const hasFieldError = Boolean(fields["email"] || fields["password"]);
        setErrors({
          email: fields["email"],
          password: fields["password"],
          general: hasFieldError ? undefined : err.message,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main id="main" className="grid min-h-screen place-items-center bg-surface p-4">
      <Card className="w-full max-w-md">
        <div className="mb-5 flex flex-col items-center gap-3 text-center">
          <img
            src="/brand/grizzly-wordmark.png"
            alt=""
            className="h-16 w-auto object-contain"
          />
          <div>
            <h1 className="font-display text-lg font-bold uppercase tracking-wide text-ink">
              {t("auth.title")}
            </h1>
            <p className="text-sm text-ink-muted">{t("auth.subtitle")}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
          {errors.general ? <ErrorState message={errors.general} /> : null}
          <TextInput
            label={t("auth.email")}
            id="login-email"
            name="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
          />
          <TextInput
            label={t("auth.passwordLabel")}
            id="login-password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
          />
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? t("auth.signingIn") : t("auth.signIn")}
          </Button>
          {import.meta.env.DEV ? (
            <p className="text-xs text-ink-muted">{t("auth.demoHint")}</p>
          ) : null}
        </form>
      </Card>
    </main>
  );
}
